import * as React from "react";

import { useProminentError } from "../../../Common";

export enum ContentRoutes {
  Login = "/customer/account/login",
  Join = "/customer/account/create",
}

export type ContentRoute = `${ContentRoutes}`;

interface ContentRouteConsumerInterface {
  hasInitialized: boolean;
  selectedContentRoute: ContentRoute;

  onUserWantsToSelectContentRoute: (route: ContentRoute) => void;
}

const defaultValue = {
  hasInitialized: false,
  selectedContentRoute: ContentRoutes.Join,
};

const ContentRouteContext = React.createContext(
  defaultValue as ContentRouteConsumerInterface
);

interface ContentRouteProviderProps {
  children?: React.ReactNode;
}

export const ContentRouteProvider: React.FunctionComponent<
  ContentRouteProviderProps
> = (props) => {
  const { setProminentError } = useProminentError();

  const [hasInitialized, setHasInitialized] = React.useState(false);

  const [selectedContentRoute, setSelectedContentRoute] =
    React.useState<ContentRoute>(ContentRoutes.Join);

  React.useEffect(() => {
    synchronizeWithUrl();
    setHasInitialized(true);
  }, []);

  const synchronizeWithUrl = () => {
    let my_route: ContentRoute;

    const browsersContentRoute = window.location.pathname
      .split("/")
      .filter((value) => value.trim().length > 0)
      .reduce((acc, urlPart, index) => {
        if (index < 3) {
          return `${acc}/${urlPart}`;
        }
        return acc;
      }, "");

    const verifiedRoute = Object.values(ContentRoutes).find(
      (a_content_route) => a_content_route === browsersContentRoute
    );
    if (verifiedRoute) {
      my_route = verifiedRoute;
    } else {
      my_route = ContentRoutes.Join;
    }

    if (my_route != selectedContentRoute) {
      setSelectedContentRoute(my_route);
    }
  };

  const updateBrowserUrl = (routeToCommit: string): void => {
    const newUrl = `${window.location.protocol}//${window.location.host}${routeToCommit}`;

    window.history.replaceState({ path: newUrl }, "", newUrl);
  };

  const onUserWantsToSelectContentRoute = (route: ContentRoute) => {
    setProminentError(null);
    setSelectedContentRoute(route);
    updateBrowserUrl(route);
  };

  return (
    <ContentRouteContext.Provider
      value={{
        hasInitialized,
        selectedContentRoute,

        onUserWantsToSelectContentRoute,
      }}
    >
      {props?.children}
    </ContentRouteContext.Provider>
  );
};

export const useContentRoute = (): ContentRouteConsumerInterface =>
  React.useContext(ContentRouteContext);
